import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CloseOutlined } from '@ant-design/icons';
import { useTabStore } from '../store/tabStore';
import './TabBar.css';

interface ContextMenu {
  x: number;
  y: number;
  tabKey: string;
}

const TabBar: React.FC = () => {
  const { tabs, activeKey, removeTab, closeOthers, closeLeft, closeRight, setActive } = useTabStore();
  const navigate = useNavigate();
  const [menu, setMenu] = useState<ContextMenu | null>(null);
  const barRef = useRef<HTMLDivElement>(null);

  // Auto-close least recently active tab when overflow detected
  useEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const check = () => {
      if (el.scrollWidth > el.clientWidth) {
        const { tabs, activeKey } = useTabStore.getState();
        const closable = tabs.filter((t) => t.closable && t.key !== activeKey);
        if (closable.length) {
          const victim = closable.reduce((a, b) => (a.lastActiveAt || 0) < (b.lastActiveAt || 0) ? a : b);
          removeTab(victim.key);
        }
      }
    };
    const ro = new ResizeObserver(check);
    ro.observe(el);
    check();
    return () => ro.disconnect();
  }, [tabs, removeTab]);

  const handleClick = (key: string, path: string) => {
    setActive(key);
    navigate(path);
  };

  const handleClose = (e: React.MouseEvent, key: string) => {
    e.stopPropagation();
    const { activeKey: currentActive } = useTabStore.getState();
    removeTab(key);
    if (currentActive === key) {
      const { activeKey: nextKey, tabs: nextTabs } = useTabStore.getState();
      const nextTab = nextTabs.find((t) => t.key === nextKey);
      if (nextTab) navigate(nextTab.path);
    }
  };

  const handleContextMenu = (e: React.MouseEvent, tabKey: string) => {
    e.preventDefault();
    setMenu({ x: e.clientX, y: e.clientY, tabKey });
  };

  const closeMenu = useCallback(() => setMenu(null), []);

  useEffect(() => {
    if (menu) {
      document.addEventListener('click', closeMenu);
      return () => document.removeEventListener('click', closeMenu);
    }
  }, [menu, closeMenu]);

  const handleMenuAction = (action: string) => {
    if (!menu) return;
    const { tabKey } = menu;
    switch (action) {
      case 'refresh':
        navigate(0 as any);
        break;
      case 'closeOthers':
        closeOthers(tabKey);
        navigate(tabs.find((t) => t.key === tabKey)!.path);
        break;
      case 'closeLeft':
        closeLeft(tabKey);
        navigate(tabs.find((t) => t.key === tabKey)!.path);
        break;
      case 'closeRight':
        closeRight(tabKey);
        navigate(tabs.find((t) => t.key === tabKey)!.path);
        break;
    }
    setMenu(null);
  };

  return (
    <div className="tab-bar" ref={barRef}>
      {tabs.map((tab) => (
        <div
          key={tab.key}
          className={`tab-item${activeKey === tab.key ? ' tab-item--active' : ''}`}
          onClick={() => handleClick(tab.key, tab.path)}
          onContextMenu={(e) => handleContextMenu(e, tab.key)}
        >
          <span className="tab-item__indicator" />
          <span className="tab-item__label">{tab.label}</span>
          {tab.closable && (
            <CloseOutlined
              className="tab-item__close"
              onClick={(e) => handleClose(e, tab.key)}
            />
          )}
        </div>
      ))}
      {menu && (
        <div className="tab-context-menu" style={{ left: menu.x, top: menu.y }}>
          <div className="tab-context-menu__item" onClick={() => handleMenuAction('refresh')}>刷新页面</div>
          <div className="tab-context-menu__item" onClick={() => handleMenuAction('closeOthers')}>关闭其它</div>
          <div className="tab-context-menu__item" onClick={() => handleMenuAction('closeLeft')}>关闭左侧</div>
          <div className="tab-context-menu__item" onClick={() => handleMenuAction('closeRight')}>关闭右侧</div>
        </div>
      )}
    </div>
  );
};

export default TabBar;
